import { durationToHuman, fluxHuman, durationToParts, dateFormat } from '../lib/utils.js';
import { ApplicationModel } from './application_model.js';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
export class Session extends ApplicationModel {
  static tableName = 'sessions';
  static attributes = this.attrAccessor(
    'startedAt',
    'endedAt',
    'fastingGoal',
    'eatingGoal',
    'eatingEndsAt',
  );

  static async after(method, { changes }){
    if(method =='destroy' || changes.has('endedAt') || changes.has('startedAt')) {
      console.log('recalculate', changes);
      this.reCalculateEatingEndsAt();
    }

  }

  static async active() {
    const collection = await this.cursorFilter((session) => !session.endedAt);
    return collection.values().next().value;
  }

  static async lastSession() {
    return this.first({ index: 'startedAt', order: 'desc' });
  }

  static fastingStates() {
    if (this.durationInHours > 1) {
      return;
    }
  }

  static goal = { fasting: 16, eating: 8 };

  static async reCalculateEatingEndsAt() {
    const sessions = await this.all({ index: 'startedAt', order: 'desc', limit: 100 });
    let prevSession = null;

    sessions.forEach((session) => {
      if (prevSession) {
        session.update({eatingEndsAt: prevSession.startedAt}, { skipCallback: true });
      }else {
        session.update({eatingEndsAt: null}, { skipCallback: true });
      }

      prevSession = session;
    });
  }

  get planHuman() {
    return `${this.fastingGoal} / ${this.eatingGoal}`;
  }

  get goal() {
    return {
      fasting: this.fastingGoal,
      eating: this.eatingGoal,
    };
  }

  get duration() {
    return this.endedAt - this.startedAt;
  }

  get durationInHours() {
    return this.duration / HOUR;
  }

  get durationInHoursRounded() {
    return Math.floor(this.durationInHours);
  }

  get isActive() {
    return !this.endedAt;
  }

  get isCompleted() {
    return !!this.endedAt;
  }

  get progress() {
    const now = Date.now();
    const diff = now - this.startedAt;
    const progressInHours = diff / HOUR;
    const progress = (progressInHours / this.goal.fasting) * 100;
    return Math.floor(progress * 100 / 100) ;
  }

  get activeDuration() {
    return Date.now() - this.startedAt;
  }

  get activeDurationInHours() {
    return Math.floor(this.activeDuration / HOUR);
  }

  get endsAtGoal() {
    return new Date(this.startedAt.getTime() + this.goal.fasting * HOUR);
  }

  get nextFastBeginsAt() {
    return this.endedAt.getTime() + this.goal.eating * HOUR;
  }

  get durationHours() {
    return Math.floor(this.duration / HOUR) % 24;
  }

  get percentComplete() {
    const diff = this.endedAt - this.startedAt;
    const progress = diff / (this.goal.fasting * HOUR);
    return Math.floor(progress * 100);
  }

  get isFailed() {
    return !this.isSuccessful;
  }

  get isSuccessful() {
    return this.percentComplete >= 100;
  }

  get flux() {
    return this.duration - this.goal.fasting * HOUR;
  }

  get fluxWithMinutePrecision() {
    return Math.round(this.flux / MINUTE) * MINUTE;
  }

  get fluxHuman() {
    return fluxHuman(this.fluxWithMinutePrecision);
  }

  get fastingFor() {
    const diff = Date.now() - this.startedAt;
    const {hours, minutes, seconds } = durationToParts(diff);
    if(hours) {
      return `${hours}h ${minutes}min`;
    }
    if (minutes) {
      return `${minutes}min`;
    }

    if (seconds) {
      return `less than a minute`;
    }

  }

  states = [
    {
      afterHours: 16,
      title: 'Autophagy (>16h)',
      desc: 'The body starts to clean up damaged cells.',
      variant: 'success',
      icon: 'bi-shield-fill-check',
    },
    {
      afterHours: 14,
      title: 'Ketosis (14-16h)',
      desc: 'The body starts to burn fat for energy.',
      variant: 'primary',
      icon: 'bi-shield-check',
    },
    {
      afterHours: 11,
      title: 'Fat burning mode (11-14h)',
      desc: 'The body starts to burn fat for energy.',
      variant: 'primary',
      icon: 'bi-fire',
    },
    {
      afterHours: 9,
      title: 'Blood sugar levels settles down (9-11h)',
      desc: 'The body is in the post-absorptive state.',
      variant: 'warning',
      icon: 'bi-plus-circle-fill',
    },
    {
      afterHours: 3,
      title: 'Blood sugar level drops (3-9h)',
      desc: 'The body starts to use stored glucose as energy.',
      variant: 'danger',
      icon: 'bi-plus-circle',
    },
    {
      afterHours: 0,
      title: 'Blood sugar level rises (0-3h)',
      desc: 'The body is in the fed state.',
      variant: 'neutral',
      icon: 'bi-arrow-up-circle',
    }
  ];

  get fastingState() {
    const hours = this.activeDurationInHours;
    const state = this.states.find((state) => {
      return hours >= state.afterHours;
    });

    return state || {};
  }

  get eatingDuration() {
    if(!this.eatingEndsAt) return null;

    return this.eatingEndsAt - this.endedAt;
  }

  get eatingDurationInHours() {
    if(!this.eatingEndsAt) return null;

    return Math.floor(this.eatingDuration / HOUR);
  }

  get eatingPercentComplete() {
    if(!this.eatingEndsAt) return null;

    const progress = (this.goal.eating * HOUR) / this.eatingDuration;
    return Math.floor(progress * 100);
  }

  get eatingIsSuccessful() {
    if(!this.eatingEndsAt) return null;

    return this.eatingPercentComplete >= 100;
  }


  static async totalFlux(statsBeginsAt) {
    const sessions = await this.all({
      index: 'startedAt',
      order: 'desc',
      query: statsBeginsAt ? IDBKeyRange.lowerBound(statsBeginsAt) : undefined,
      limit: 365,
    });
    return [...sessions.values()].reduce((sum, session) => {
      const result = sum + session.fluxWithMinutePrecision || 0;
      return result;
    }, 0);
  }

  static async totalFluxHuman(statsBeginsAt) {
    const fluxTotal = await this.totalFlux(statsBeginsAt);
    return fluxHuman(fluxTotal);
  }

  // Flux difference between actual plan and current totalFlux from -1 to 1
  static async normalizedFluxOffset(statsBeginsAt) {
    const fastingGoal = await this.averageFastingGoal();
    const totalFlux = await this.totalFlux(statsBeginsAt);

    // const fastingGoal = 16;
    // const totalFlux = -0.1 * HOUR;

    if (!totalFlux) return 0;

    const fastingGoalInMs = fastingGoal * HOUR;

    const fluxOffset = totalFlux / fastingGoalInMs;

    console.log({fluxOffset, fastingGoal, fastingGoalInMs, totalFlux});

    const absFluxOffset = Math.abs(fluxOffset);

    if (fluxOffset < -1) return -1;
    else if (fluxOffset > 1) return 1;
    else if (absFluxOffset > 0.98 || absFluxOffset < 0.02) return 0;

    return fluxOffset;
  }

  static async totalFastingDays(statsBeginsAt) {
    const firstSession = await this.first({
      index: 'startedAt',
      query: statsBeginsAt ? IDBKeyRange.lowerBound(statsBeginsAt) : undefined,
    });

    if (!firstSession) return 0;
    const currentDate = new Date();

    const diff = currentDate - firstSession.startedAt;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    return days;
  }

  static async averageFastingGoal() {
    const sessions = await this.all({
      index: 'startedAt',
      order: 'desc',
      limit: 30,
    });

    const totalFastingGoal = [...sessions.values()].reduce((sum, session) => {
      return sum + (session.fastingGoal || 0);
    }, 0);


    const result = Math.floor(totalFastingGoal / sessions.size) || 0;

    return result;
  }

  static async calculateActualPlan(statsBeginsAt) {
    const sessions = await this.all({
      index: 'startedAt',
      order: 'desc',
      limit: 30,
      query: statsBeginsAt ? IDBKeyRange.lowerBound(statsBeginsAt) : undefined,
    });

    const endedSessions = [...sessions.values()].filter(s => s.endedAt);

    if (!endedSessions.length) return { fasting: 0, eating: 0 };

    const totalFastingDuration = endedSessions.reduce((sum, session) => {
      return sum + (session.durationInHours || 0);
    }, 0);

    const totalEatingSessions = endedSessions.filter(s => s.eatingEndsAt);
    const totalEatingDuration = totalEatingSessions.reduce((sum, session) => {
      return sum + (session.eatingDurationInHours || 0);
    }, 0);

    const fasting = Math.floor(totalFastingDuration / endedSessions.length) || 0;
    const eating = Math.floor(totalEatingDuration / totalEatingSessions.length) || 0;

    return { fasting, eating };
  }

  static async fluxHistory(statsBeginsAt) {
    const sessions = await this.all({
      index: 'startedAt',
      order: 'asc',
      query: statsBeginsAt ? IDBKeyRange.lowerBound(statsBeginsAt) : undefined,
    });

    let cumulativeFlux = 0;
    const history = [];

    for (const [_id, session] of sessions) {
      if (!session.endedAt) continue;

      cumulativeFlux += session.fluxWithMinutePrecision || 0;

      history.push({
        date: session.startedAt,
        flux: cumulativeFlux
      });
    }

    return history;
  }
}


async function seed() {
  await Session.truncate();

await   Session.create({
    startedAt: new Date('2025-03-17T21:30'),
    endedAt: new Date('2025-03-18T13:05'),
    fastingGoal: 22,
    eatingGoal: 2,
  });

  await Session.create({
    startedAt: new Date('2025-03-18T21:15'),
    endedAt: new Date('2025-03-19T13:00'),
    fastingGoal: 16,
    eatingGoal: 8,
  });

  await Session.create({
    startedAt: new Date('2025-03-19T21:00'),
    endedAt: new Date('2025-03-20T13:00'),
    fastingGoal: 16,
    eatingGoal: 8,
  });

  await Session.create({
    startedAt: new Date('2025-03-22T20:30'),
    endedAt: new Date('2025-03-23T13:00'),
    fastingGoal: 16,
    eatingGoal: 8,
  });

  await Session.create({
    startedAt: new Date('2025-03-26T21:28'),
    endedAt: new Date('2025-03-27T13:00'),
    fastingGoal: 16,
    eatingGoal: 8,
  });

  await Session.create({
    startedAt: new Date('2025-03-27T00:00'),
    fastingGoal: 16,
    eatingGoal: 8,
  });

  await Session.create({
    startedAt: new Date('2025-04-04T12:00'),
    fastingGoal: 16,
    eatingGoal: 8,
  });
}

function isLocal() {
  const { hostname } = window.location;
  return ['localhost', '0.0.0.0', '127.0.0.1'].includes(hostname);
}

if(isLocal()) {
  // console.log('seeding');
  // Session.truncate();
  // await seed();
}

Session.reCalculateEatingEndsAt();

window.Session = Session;

